import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';

import { useIsMobileDevice } from '@hivebrite/react-hooks';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import ConfirmationModal from 'hb-react/shared/components/DesignSystem/Modal/ConfirmationModal';
import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

import Form from './Form';

const TripFormModal = ({
  tripId,
  trip: tripFromProps,
  onCloseModal,
  onCreate,
  onUpdate,
}) => {
  const { currentUser } = useDefaultAppContexts();
  const isMobileDevice = useIsMobileDevice();

  const modalRef = useRef();
  const formRef = useRef();

  const [trip, setTrip] = useState(tripFromProps || null);
  const [displayCloseModal, setDisplayCloseModal] = useState(false);
  const [modalElement, setModalElement] = useState();
  const [isDisplayedEmojiPicker, setIsDisplayedEmojiPicker] = useState(false);

  const isUpdatingTrip = !!tripId;
  const modalHeaderId = modalRef.current
    ?.getHeaderElement()
    ?.getAttribute('id');

  const canDisplayEmojiPicker = !isMobileDevice;

  const handleToggleEmojiPicker = (isToggled) => {
    setIsDisplayedEmojiPicker(isToggled);
  };

  // Update modalElement if modal updated
  useEffect(() => {
    setModalElement(modalRef.current?.getModalElement());
  }, [modalRef.current]);

  useEffect(() => {
    // Do not fetch if the trip is already passed to the Component
    if (tripFromProps) {
      return undefined;
    }

    if (isUpdatingTrip) {
      // Edit existing trip
      axios
        .get(Routes.user_trip_path(currentUser.id, tripId))
        .then(({ data }) => {
          setTrip(data?.trip_location);
        });
    } else {
      // Create draft for new trip
      axios.get(Routes.new_user_trip_path(currentUser.id)).then(({ data }) => {
        setTrip(data?.trip_location);
      });
    }
  }, []);

  return (
    <>
      <ModalWithTemplate
        header={
          isUpdatingTrip
            ? __('Edit your trip')
            : __('Create a trip - global feed')
        }
        isLoading={isEmpty(trip)}
        onCloseModal={() =>
          formRef.current?.canDisplayDiscardChangesModal()
            ? setDisplayCloseModal(true)
            : onCloseModal()
        }
        ref={modalRef}
        screenReadersCloseButtonText={
          isUpdatingTrip
            ? s__('Accessibility|Close edit a trip window')
            : s__('Accessibility|Close create a trip window')
        }
        enableEscAction={!isDisplayedEmojiPicker}
        // Needed to handle native share button
        pb={{ s: '85px', m: 6 }}
      >
        <Form
          canDisplayEmojiPicker={canDisplayEmojiPicker}
          isLoading={isEmpty(trip)}
          isUpdatingTrip={isUpdatingTrip}
          modalElement={modalElement}
          modalHeaderId={modalHeaderId}
          onCloseModal={onCloseModal}
          onCreate={onCreate}
          onToggleEmojiPicker={handleToggleEmojiPicker}
          onUpdate={onUpdate}
          ref={formRef}
          trip={trip}
        />
      </ModalWithTemplate>
      {displayCloseModal && (
        <ConfirmationModal
          onCloseModal={() => setDisplayCloseModal(false)}
          confirmButtonLabel={
            isUpdatingTrip
              ? s__('LiveFeed|Discard changes')
              : s__('LiveFeed|Discard draft')
          }
          onConfirmation={onCloseModal}
          text={
            isUpdatingTrip
              ? s__(
                  'LiveFeed|Your changes will be discarded. Are you sure you want to exit?',
                )
              : s__(
                  'LiveFeed|Your draft will be discarded. Are you sure you want to exit?',
                )
          }
          title={
            isUpdatingTrip
              ? s__('LiveFeed|Discard changes')
              : s__('LiveFeed|Discard draft')
          }
        />
      )}
    </>
  );
};

TripFormModal.propTypes = {
  onCloseModal: PropTypes.func.isRequired,
  onCreate: PropTypes.func,
  onUpdate: PropTypes.func,
  tripId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default TripFormModal;
